import React from 'react'
import { useSearchParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Spinner, Tabs } from '@factorialco/f0-react/dist/experimental'
import { ProcurementTypeItemFragment, WorkflowContainerFragment } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { Form } from 'modules/forms'
import { QuestionnaireType } from 'modules/forms/components/newFormEditor/types'

interface FormProps {
  isNewFormEditorEnabled: false
  form: Form | undefined
  setForm: (form: Form) => void
}

interface NewFormProps {
  isNewFormEditorEnabled: true
  formModel: Form | undefined
  formState: QuestionnaireType
  setFormState: (state: QuestionnaireType) => void
  isFormLoading: boolean
}

type TabContentProps = {
  id: string | undefined
  requestType: ProcurementTypeItemFragment | undefined
  workflow: WorkflowContainerFragment | undefined
} & (FormProps | NewFormProps)

const FormTab = React.lazy(() => import('modules/procurement/components/procurementTypes/Form'))
const WorkflowsTab = React.lazy(
  () => import('modules/procurement/components/procurementTypes/Workflows')
)

const TabContent = (props: TabContentProps): React.JSX.Element => {
  const { id, requestType, workflow } = props
  const { navigation } = useNavigation()
  const [searchParams] = useSearchParams()

  // The active tab is from the params or 'form' by default
  const activeTab = React.useMemo(() => searchParams.get('tab') || 'form', [searchParams])

  const formContent = (() => {
    if (!props.isNewFormEditorEnabled) {
      if (!props.requestType) return null
      return (
        <FormTab
          isNewFormEditorEnabled={false}
          requestType={props.requestType}
          form={props.form}
          setForm={props.setForm}
        />
      )
    }

    if (props.isFormLoading) {
      return (
        <Box
          flexGrow
          height="full"
          padding="s16"
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
        >
          <Spinner />
        </Box>
      )
    }

    return <FormTab isNewFormEditorEnabled state={props.formState} setState={props.setFormState} />
  })()

  const workflowForm = props.isNewFormEditorEnabled ? props.formModel : props.form

  const Content = React.useMemo<React.ReactNode>(() => {
    if (!requestType || !id) {
      return null
    }

    switch (activeTab) {
      case 'workflows':
        return (
          <WorkflowsTab requestType={requestType} workflow={workflow ?? null} form={workflowForm} />
        )
      case 'form':
      default:
        return formContent
    }
  }, [activeTab, id, requestType, workflow, workflowForm, formContent])

  const path = React.useMemo(() => {
    return navigation.settings.procurement.requestType.id.fullPath({
      id: id || '',
    })
  }, [navigation, id])

  return (
    <>
      <Tabs
        tabs={[
          {
            href: `${path}?tab=form`,
            label: i18n.t('procurement.purchase_requests.settings.types.detail.tabs.form.title'),
            index: true,
          },
          {
            href: `${path}?tab=workflows`,
            label: i18n.t(
              'procurement.purchase_requests.settings.types.detail.tabs.workflows.title'
            ),
          },
        ].filter(Boolean)}
      />

      {Content}
    </>
  )
}

export default React.memo(TabContent)
